import { ILoanPaymentPlanModel, routes } from '@linqpal/models'
import { IDownPaymentDetails } from '@linqpal/models/src/types/routes'
import { observer } from 'mobx-react'
import { useTranslation } from 'react-i18next'
import ApplicationStore from '../../../../GeneralApplication/Application/ApplicationStore'
import { useNavigation } from '@react-navigation/native'
import React, { useState } from 'react'
import { useResponsive } from '../../../../../utils/hooks'
import { isWeb } from '../../../../../utils/helpers/commonUtils'
import { fileDownloader } from '@linqpal/common-frontend/src/helpers'
import { Linking, View } from 'react-native'
import { PaymentPlanCard } from '../../../../../ui/atoms/PaymentPlanCard'
import { Spacer } from '../../../../../ui/atoms'
import { PropertyList } from '../../../../../ui/molecules/PropertyList'
import { toCurrency } from '@linqpal/models/src/helpers/formatter'
import { BtInput } from '@linqpal/components/src/ui'
import { Agreement } from '../../../InvoicesTab/PayInvoice/Agreement'
import { InvoiceCreditPaymentButton } from './InvoiceCreditPaymentButton'
import RootStore from '../../../../../store/RootStore'
import { DownPaymentMethod } from '@linqpal/models/src/dictionaries/downPayment'

interface IProps {
  invoiceIds: string[]
  paymentPlan: ILoanPaymentPlanModel
  downPayment: IDownPaymentDetails | null
  navigation?: any
  onSuccess: () => void
}

export const InvoiceCreditPaymentForm = observer(
  ({
    invoiceIds,
    paymentPlan,
    downPayment,
    onSuccess,
    navigation: navigationProp,
  }: IProps) => {
    const { t } = useTranslation('global')
    const { sm } = useResponsive()

    const { invoiceDetails, bankAccount: account } = ApplicationStore
    const { getCardFee } = RootStore.screensStore?.paymentMethodsStore

    const navigation = navigationProp ?? useNavigation()

    const [downloading, setDownloading] = useState(false)

    const invoice = invoiceDetails[0]
    const isDirectTerms = invoice?.supplierInvitationDetails

    const invoiceAmount = invoiceDetails.reduce(
      (sum, inv) => sum + inv.total_amount,
      0,
    )

    const loanFee = Math.round(invoiceAmount * paymentPlan.fee) / 100
    const cardFee =
      downPayment?.paymentMethod === DownPaymentMethod.Card &&
      downPayment.accountId
        ? getCardFee(downPayment.amount, downPayment.accountId)
        : 0

    const totalAmount = invoiceAmount + loanFee + cardFee

    const paymentData = [
      {
        name: t('payInvoice.To'),
        value: isDirectTerms
          ? invoice.supplierInvitationDetails?.name
          : invoice?.company?.name,
      },
      {
        name: t('payInvoice.invoice-amount'),
        value: toCurrency(invoiceAmount),
      },
      {
        name: t('payInvoice.loan-fee'),
        value: toCurrency(loanFee),
      },
    ]

    if (downPayment?.paymentMethod === DownPaymentMethod.Card) {
      paymentData.push({
        name: t('payInvoice.down-payment-card-fee'),
        value: toCurrency(cardFee),
      })
    }

    paymentData.push({
      name: downPayment
        ? t('payInvoice.total-amount-with-down-payment')
        : t('payInvoice.total-amount'),
      value: toCurrency(totalAmount),
    })

    const generateAgreement = () => {
      setDownloading(true)
      routes.contractor
        .makeAgreement({ invoiceIds, paymentPlan })
        .then((r) => {
          if (r.url) {
            if (isWeb) {
              fileDownloader(r.url, {}, r.fileName)
            } else {
              Linking.openURL(r.url)
            }
          }
        })
        .finally(() => setDownloading(false))
    }

    return (
      <View>
        <PaymentPlanCard
          onPress={() => {}}
          paymentOption={paymentPlan}
          paymentAmount={invoiceAmount}
          downPayment={downPayment}
          selected
          forSummaryPage
        />
        <Spacer height={30} />

        <PropertyList
          // eslint-disable-next-line i18next/no-literal-string
          theme="white"
          data={paymentData}
        />
        <Spacer height={30} />

        <BtInput
          size={'large'}
          label={t('payInvoice.select-autodebit-account')}
          value={(account?.accountName || account?.name || '').split('/')[0]}
          disabled={true}
        />
        {sm && <Spacer height={30} />}

        <Agreement onPress={generateAgreement} downloading={downloading} />
        <Spacer height={sm ? 38 : 20} />

        <View style={{ marginLeft: 'auto' }}>
          <InvoiceCreditPaymentButton
            invoiceIds={invoiceDetails.map((i) => i._id)}
            paymentPlan={paymentPlan}
            downPayment={downPayment}
            isDirectTerms={isDirectTerms}
            navigation={navigation}
            onSuccess={onSuccess}
          />
        </View>
        <Spacer height={30} />
      </View>
    )
  },
)
